import React, { useCallback, useEffect, useState } from 'react'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import ProfileAmlMatchReviewPanel from '../../../components/aml/ProfileAmlMatchReviewPanel'

import { formatSydneyDate, formatSydneyDateTime } from '../../../utils/sydneyDateTime'
/**
 * AML / PEP screening rail — same provider checks as Watchlist & Investigation
 * (`POST /api/aml/checks/run`), with results in a right-side drawer.
 */
export default function EcddAmlPepPanel({
  memberId,
  memberName,
  reason = 'ECDD re-screen',
  onOpenOsintScreening,
  showOsintButton = false,
  /** Called after AML history loads — parent can stamp ECDD form / sign-off gates. */
  onHistoryLoaded = null,
  /** Called after a successful AML/PEP re-screen so the ECDD form can stamp date / link the check. */
  onScreened = null,
  /** `inline` = embed in Screening refresh; `rail` = standalone right-column card */
  variant = 'rail',
  /** 'staff' = employee screening: stored in AML history but never touches patron risk / PEP flags. */
  subjectType = null,
}) {
  const [amlChecks, setAmlChecks] = useState([])
  const [amlLoading, setAmlLoading] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [error, setError] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerData, setDrawerData] = useState(null)
  const [reviewSaving, setReviewSaving] = useState(false)

  const loadAmlChecks = useCallback(async () => {
    if (!memberId) {
      setAmlChecks([])
      return
    }
    setLoadingHistory(true)
    setError('')
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/aml/checks?member_id=${encodeURIComponent(memberId)}&limit=8`)
      )
      const json = await res.json().catch(() => ({}))
      if (!json.success) throw new Error(json.error || 'Failed to load AML history')
      const rows = Array.isArray(json.data) ? json.data : []
      setAmlChecks(rows)
      if (typeof onHistoryLoaded === 'function') {
        onHistoryLoaded({ count: rows.length, latest: rows[0] || null })
      }
    } catch (e) {
      setError(e.message || 'Failed to load AML history')
      setAmlChecks([])
    } finally {
      setLoadingHistory(false)
    }
  }, [memberId])

  useEffect(() => {
    loadAmlChecks()
  }, [loadAmlChecks])

  const openDrawer = (data) => {
    setDrawerData(data)
    setDrawerOpen(true)
  }

  const handleReScreen = async () => {
    const name = String(memberName || '').trim()
    if (!memberId || !name) {
      setError('Member ID and name are required to run AML/PEP screening.')
      return
    }
    setAmlLoading(true)
    setError('')
    try {
      const res = await authFetch(getLocalApiUrl('/api/aml/checks/run'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_id: memberId,
          name,
          country: 'AUST',
          reason,
          performed_by: 'web_user',
          ...(subjectType ? { subject_type: subjectType } : {}),
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!json?.success) throw new Error(json?.error || 'AML check failed')
      await loadAmlChecks()
      const d = json.data
      if (typeof onScreened === 'function') {
        const sample = Array.isArray(d?.sample) ? d.sample : []
        onScreened({
          checkId: d?.id || null,
          hitCount: sample.length,
          name: d?.name || name,
          at: new Date().toISOString(),
        })
      }
      if (d?.id) {
        openDrawer({
          checkId: d.id,
          name: d.name || name,
          country: d.country,
          results: Array.isArray(d.sample) ? d.sample : [],
        })
      }
    } catch (e) {
      setError(e?.message || 'Re-screen failed')
    } finally {
      setAmlLoading(false)
    }
  }

  const viewAmlCheckDetails = async (checkId) => {
    if (!checkId) return
    setAmlLoading(true)
    setError('')
    try {
      const res = await authFetch(getLocalApiUrl(`/api/aml/checks/${encodeURIComponent(checkId)}`))
      const json = await res.json().catch(() => ({}))
      if (!json?.success) throw new Error(json?.error || 'Failed to load check')
      const d = json.data || {}
      const sample = Array.isArray(d.result_sample)
        ? d.result_sample
        : Array.isArray(d.results)
          ? d.results
          : Array.isArray(d.sample)
            ? d.sample
            : []
      openDrawer({
        checkId: d.id || checkId,
        name: d.full_name || d.name || memberName,
        country: d.country_code || d.country,
        results: sample,
        analystDecision: d.analyst_decision || null,
        analystReason: d.analyst_reason || '',
      })
    } catch (e) {
      setError(e?.message || 'Failed to open check')
    } finally {
      setAmlLoading(false)
    }
  }

  const latest = amlChecks[0]
  const latestMatches = Number(latest?.result_count) || 0
  const results = drawerData?.results || []

  const body = (
    <div className={variant === 'inline' ? 'space-y-3' : 'px-4 py-3.5 space-y-3'}>
      {!memberId ? (
        <p className="text-sm text-slate-500 m-0">No member linked — screening needs an ID.</p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">AML / PEP status</div>
              {loadingHistory ? (
                <div className="text-sm text-slate-500 mt-0.5">Loading…</div>
              ) : latest ? (
                <div className="flex items-center gap-1.5 mt-0.5">
                  {latestMatches > 0 ? (
                    <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 shrink-0" />
                  ) : (
                    <CheckCircleIcon className="w-4 h-4 text-green-600 shrink-0" />
                  )}
                  <span className={`text-sm font-semibold ${latestMatches > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                    {latestMatches > 0 ? `${latestMatches} for review` : 'Clear'}
                  </span>
                </div>
              ) : (
                <div className="text-sm text-slate-500 mt-0.5">No screening on record</div>
              )}
            </div>
            <Button
              type="button"
              size="sm"
              className="bg-psg-teal hover:bg-psg-teal-700 shrink-0"
              disabled={amlLoading || !memberName}
              onClick={handleReScreen}
            >
              <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
              {amlLoading ? 'Screening…' : 'Run AML / PEP'}
            </Button>
          </div>

          {latest && (
            <div className="text-xs text-slate-500">
              SCR-{latest.id}
              {latest.created_at
                ? ` · ${formatSydneyDateTime(new Date(latest.created_at))}`
                : ''}
            </div>
          )}

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
              AML / PEP history
            </div>
            {amlChecks.length === 0 ? (
              <p className="text-xs text-slate-500 m-0">
                No checks yet — click Run AML / PEP to screen.
              </p>
            ) : (
              <ul className="space-y-2 m-0 p-0 list-none">
                {amlChecks.map((c) => (
                  <li
                    key={c.id}
                    className="flex items-start gap-2 rounded-lg border border-slate-100 px-2.5 py-2"
                  >
                    {Number(c.result_count) > 0 ? (
                      <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                    ) : (
                      <CheckCircleIcon className="w-3.5 h-3.5 text-green-600 mt-0.5 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-slate-700">
                        SCR-{c.id} · {c.result_count || 0} match{(c.result_count || 0) !== 1 ? 'es' : ''}
                      </div>
                      <div className="text-[11px] text-slate-400 truncate">
                        {c.reason || 'Screening'}
                        {c.created_at
                          ? ` · ${formatSydneyDate(new Date(c.created_at))}`
                          : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="text-xs font-semibold text-psg-teal hover:underline inline-flex items-center gap-0.5 shrink-0 bg-transparent border-none p-0 cursor-pointer"
                      onClick={() => viewAmlCheckDetails(c.id)}
                    >
                      <EyeIcon className="w-3.5 h-3.5" /> View
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {showOsintButton && typeof onOpenOsintScreening === 'function' && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full border-psg-teal/40 text-psg-teal hover:bg-psg-teal/5"
              onClick={onOpenOsintScreening}
            >
              <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
              OSINT screening sources
            </Button>
          )}
        </>
      )}

      {error && <p className="text-xs text-red-600 m-0">{error}</p>}
    </div>
  )

  return (
    <>
      {variant === 'inline' ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50/40 overflow-hidden mb-4">
          <div className="px-3.5 py-2.5 border-b border-slate-100 flex items-center gap-2">
            <ShieldCheckIcon className="w-4 h-4 text-psg-teal shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#073B4D]">AML / PEP screening</div>
              <div className="text-xs text-slate-500">Same provider screen as Watchlist &amp; Investigation</div>
            </div>
          </div>
          <div className="px-3.5 py-3">{body}</div>
        </div>
      ) : (
        <aside className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-3.5 border-b border-slate-100 bg-slate-50/80">
            <div className="flex items-start gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-[#0A516915] flex items-center justify-center shrink-0">
                <ShieldCheckIcon className="w-5 h-5 text-psg-teal" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-[#073B4D]">AML / PEP Screening</div>
                <div className="text-xs text-slate-500 mt-0.5 leading-snug">
                  Same provider screen as Watchlist & Investigation
                </div>
              </div>
            </div>
          </div>
          {body}
        </aside>
      )}

      {drawerOpen && drawerData && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setDrawerOpen(false)}
            aria-hidden
          />
          <div className="fixed right-0 top-0 h-full w-full max-w-[1100px] bg-white shadow-xl z-50 flex flex-col">
            <div className="flex items-center justify-between py-3.5 px-4 border-b border-slate-200 shrink-0">
              <div className="min-w-0">
                <div className="text-base font-bold text-slate-800 truncate">
                  {results.length === 0 ? 'Screening clear' : 'AML/PEP match review'}
                </div>
                <div className="text-xs text-slate-500">
                  {drawerData.name}
                  {drawerData.checkId ? ` · Check #${drawerData.checkId}` : ''}
                  {drawerData.country ? ` · ${drawerData.country}` : ''}
                  {results.length > 0 ? ` · ${results.length} results` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 rounded-md border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 min-h-0">
              {results.length === 0 ? (
                <div className="rounded-lg p-6 border border-green-200 bg-green-50 text-center">
                  <CheckCircleIcon className="w-7 h-7 text-green-600 mx-auto mb-2" />
                  <div className="text-base font-bold text-green-700">No matches found</div>
                  <div className="text-xs text-slate-600 mt-1">No PEP · No Sanctions · No Adverse Media</div>
                </div>
              ) : (
                <ProfileAmlMatchReviewPanel
                  results={results}
                  searchName={drawerData.name || ''}
                  checkId={drawerData.checkId || null}
                  initialDecision={drawerData.analystDecision || null}
                  initialReason={drawerData.analystReason || ''}
                  saving={reviewSaving}
                  onSaveReview={async ({ decision, reason, selected_match }) => {
                    if (!drawerData.checkId) throw new Error('Check id missing')
                    setReviewSaving(true)
                    try {
                      const resp = await authFetch(
                        getLocalApiUrl(`/api/aml/checks/${encodeURIComponent(String(drawerData.checkId))}/review`),
                        {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ decision, reason, selected_match }),
                        }
                      )
                      const data = await resp.json().catch(() => ({}))
                      if (!resp.ok || !data?.success) throw new Error(data?.error || 'Failed to save review')
                      setDrawerData((prev) => prev ? { ...prev, analystDecision: decision, analystReason: reason || '' } : prev)
                      await loadAmlChecks()
                    } finally {
                      setReviewSaving(false)
                    }
                  }}
                />
              )}
            </div>
          </div>
        </>
      )}
    </>
  )
}
